/**
 * Container  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { Type, defineElementaryComponent, createSubScope } from "@adapptio/lib-blueprint";

import {
	TViewComponentDefinition,
	TComponentReact,
	Container,
	ContainerProps,
	isEventEnabled
} from "@adapptio/lib-components";
import { termsEditor } from "../../terms";
import { BaseEvents } from "../../base/BaseEvents";

interface ComponentContainer_State {}

const ComponentContainer_Props = {
	...ContainerProps
};

const ComponentContainer_Events = {
	...BaseEvents
};

const ComponentContainer_Definition = defineElementaryComponent<
	typeof ComponentContainer_Props,
	ComponentContainer_State,
	typeof ComponentContainer_Events
>({
	...termsEditor.components.container.component,

	name: "container",

	category: "layout",

	icon: "mdi/selection",

	docUrl: "...",

	order: 20,

	container: true,

	props: ComponentContainer_Props,

	events: ComponentContainer_Events,

	resolve: (_spec, state) => {
		return state || {};
	},

	getScopeData: () => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentContainer_React: TComponentReact<typeof ComponentContainer_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {
	// Event handler

	const _clickHandler = React.useMemo(() => {
		if (isEventEnabled(componentInstance.eventEnabled.click, componentInstance.componentMode)) {
			return () => componentInstance.eventTriggers.click((parentScope) => createSubScope(parentScope));
		}

		return;
	}, [
		componentInstance.componentMode,
		componentInstance.eventEnabled.click,
		componentInstance.eventTriggers.click
	]);

	return (
		<Container
			{...props}
			componentPath={componentInstance.safePath}
			componentMode={componentInstance.componentMode}
			classList={reactComponentClassList}
			contentModelNode={componentInstance.modelNode?.props.props.content as unknown as any}
			onClick={_clickHandler}
		/>
	);
};

export const ComponentContainer: TViewComponentDefinition<typeof ComponentContainer_Definition> = {
	...ComponentContainer_Definition,
	reactComponent: ComponentContainer_React
};
